import { useState, useEffect, useRef, useCallback } from 'react'
import { streamApplicationLogs } from '@/lib/api'

export interface LogEntry {
  id: string
  line: string
  timestamp?: string
  pod?: string
  container?: string
}

export function useApplicationLogs(appId: string) {
  const [logsOpen, setLogsOpen] = useState(false)
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [logsError, setLogsError] = useState<string | null>(null)
  const [logsStreaming, setLogsStreaming] = useState(false)
  const [followLogs, setFollowLogs] = useState(true)
  const [tailLines, setTailLines] = useState(200)
  const [logsFullScreen, setLogsFullScreen] = useState(false)
  const [activePod, setActivePod] = useState('')
  const [activeContainer, setActiveContainer] = useState('')
  const logsAbortRef = useRef<AbortController | null>(null)
  const logsContainerRef = useRef<HTMLDivElement | null>(null)

  const stopLogsStream = useCallback(() => {
    logsAbortRef.current?.abort()
    logsAbortRef.current = null
  }, [])

  const startLogsStream = useCallback(async () => {
    if (!appId) {
      return
    }
    stopLogsStream()
    setLogs([])
    setLogsError(null)
    setLogsStreaming(true)
    setActivePod('')
    setActiveContainer('')
    const controller = new AbortController()
    logsAbortRef.current = controller

    try {
      for await (const entry of streamApplicationLogs(appId, {
        follow: followLogs,
        tailLines,
        signal: controller.signal,
      })) {
        const timestampSeconds = entry.timestamp?.seconds
        const timestampValue = typeof timestampSeconds === 'bigint'
          ? Number(timestampSeconds)
          : typeof timestampSeconds === 'number'
            ? timestampSeconds
            : undefined
        const timestamp = timestampValue !== undefined
          ? new Date(timestampValue * 1000).toLocaleTimeString()
          : undefined
        setLogs((prev) => ([
          ...prev,
          {
            id: `${prev.length}-${Date.now()}`,
            line: entry.logLine,
            timestamp,
            pod: entry.podName || undefined,
            container: entry.containerName || undefined,
          }
        ]))
        if (entry.podName) {
          setActivePod((current) => current || entry.podName)
        }
        if (entry.containerName) {
          setActiveContainer((current) => current || entry.containerName)
        }
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setLogsError(error instanceof Error ? error.message : 'Failed to stream logs')
      }
    } finally {
      setLogsStreaming(false)
    }
  }, [appId, followLogs, tailLines, stopLogsStream])

  const clearLogs = () => {
    setLogs([])
  }

  useEffect(() => {
    if (logsOpen) {
      void startLogsStream()
      return stopLogsStream
    }
    stopLogsStream()
    setLogs([])
    setLogsError(null)
    return
  }, [logsOpen, startLogsStream, stopLogsStream])

  useEffect(() => {
    if (!followLogs || !logsContainerRef.current) {
      return
    }
    logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight
  }, [logs, followLogs])

  return {
    logs,
    logsOpen,
    logsError,
    logsStreaming,
    followLogs,
    tailLines,
    logsFullScreen,
    activePod,
    activeContainer,
    setLogsOpen,
    setFollowLogs,
    setTailLines,
    setLogsFullScreen,
    startLogsStream,
    clearLogs,
    logsContainerRef,
  }
}
